import { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@radix-ui/react-dialog';
import { Button } from './ui/Button';
import { Loader2, Play, Settings2 } from 'lucide-react';

interface RunNowButtonProps {
  siteId?: number;
  policyId?: number;
  onRunStarted?: (jobId: string) => void;
  variant?: 'default' | 'outline' | 'secondary';
  size?: 'default' | 'sm' | 'lg';
}

export function RunNowButton({
  siteId,
  policyId,
  onRunStarted,
  variant = 'default',
  size = 'default',
}: RunNowButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dryRun, setDryRun] = useState(false);

  const handleRunNow = async () => {
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/reminders/run-now`,
        {
          site_id: siteId,
          policy_id: policyId,
          dry_run: dryRun,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(
          dryRun
            ? 'Dry run queued successfully!'
            : 'Reminder run queued successfully!'
        );
        onRunStarted?.(response.data.data.job_id);
        setIsOpen(false);
      } else {
        throw new Error(response.data.error?.message || 'Failed to queue run');
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.error?.message || 'Failed to start run');
      } else {
        toast.error('An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size}>
          <Play className="w-4 h-4 mr-2" />
          Run Now
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] bg-white rounded-lg shadow-lg p-6">
        <div className="space-y-2">
          <DialogTitle className="text-lg font-semibold">
            Start Reminder Run
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            This will trigger an immediate reminder run based on your reminder policies.
            {siteId && (
              <span className="block mt-1">Site ID: {siteId}</span>
            )}
            {policyId && (
              <span className="block mt-1">Policy ID: {policyId}</span>
            )}
          </DialogDescription>
        </div>

        <div className="py-4 space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="dryRun"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="dryRun"
              className="text-sm font-medium text-gray-700 cursor-pointer"
            >
              Dry run (simulate without sending messages)
            </label>
          </div>

          <div className="bg-blue-50 p-3 rounded-md">
            <div className="flex items-start">
              <Settings2 className="w-5 h-5 text-blue-500 mr-2 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">What happens next?</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Find candidates based on reminder policies</li>
                  <li>Queue reminders to be sent</li>
                  <li>Process reminders via BullMQ workers</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleRunNow} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Queuing...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Start Run
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Simple button without dialog (for quick actions)
export function QuickRunButton({
  siteId,
  policyId,
  onRunStarted,
}: Omit<RunNowButtonProps, 'variant' | 'size'>) {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/reminders/run-now`,
        {
          site_id: siteId,
          policy_id: policyId,
          dry_run: false,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.data.success) {
        toast.success('Reminder run started!');
        onRunStarted?.(response.data.data.job_id);
      }
    } catch (error) {
      toast.error('Failed to start run');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button size="sm" onClick={handleClick} disabled={isLoading}>
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Play className="w-4 h-4" />
      )}
    </Button>
  );
}

